/* eslint-disable no-underscore-dangle */
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import {
  Box,
  Stack,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  Alert,
} from '@mui/material'
import _get from 'lodash/get'

import AuthContext from '../../contexts/AuthContext'
import Breadcrumb from '../../components/BreadcrumbLayout'
import { LogTableSection } from '../../components/logs'
import { ActivitySnapshotDialog } from '../../components/activities'
import activityLogsPaginationQuery from '../../graphql/query/activityLogsPagination'
import usersQuery from '../../graphql/query/users'
import eventByIdQuery from '../../graphql/query/eventById'

/**
 * Mounted at both /logs (all events) and /event/:eventId/logs (scoped) — `eventId` is
 * undefined on the global route, which is what switches the Event column on.
 */
const LogsPage = () => {
  const { eventId } = useParams()
  const { user } = useContext(AuthContext)

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [actorId, setActorId] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(25)
  const [snapshotRow, setSnapshotRow] = useState(null)

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 400)
    return () => clearTimeout(timer)
  }, [searchInput])

  useEffect(() => {
    setPage(1)
  }, [search, actorId, dateFrom, dateTo, perPage])

  const filter = useMemo(() => {
    const next = {}
    if (eventId) next.eventId = eventId
    if (actorId) next.actorId = actorId
    if (search) next.search = search
    if (dateFrom) next.deletedAtFrom = new Date(`${dateFrom}T00:00:00`).toISOString()
    if (dateTo) next.deletedAtTo = new Date(`${dateTo}T23:59:59.999`).toISOString()
    return next
  }, [eventId, actorId, search, dateFrom, dateTo])

  const logs = useQuery(activityLogsPaginationQuery, {
    variables: { filter, sort: 'DELETEDAT_DESC', page, perPage },
    fetchPolicy: 'cache-and-network',
  })
  const users = useQuery(usersQuery, { variables: {} })
  const event = useQuery(eventByIdQuery, {
    variables: { _id: eventId },
    skip: !eventId,
  })

  const items = _get(logs, 'data.activityLogsPagination.items', [])
  const totalCount = _get(logs, 'data.activityLogsPagination.count', 0)
  const pageCount = _get(logs, 'data.activityLogsPagination.pageInfo.pageCount', 1)
  const userOptions = _get(users, 'data.users', []) || []

  // Early returns - MUST be after all hooks
  if (user.role !== 'Admin') {
    return 'Cannot Access'
  }

  const eventName = _get(event, 'data.eventId.name', '')

  return (
    <Box>
      {eventId ? (
        <Breadcrumb
          path="/events"
          titleLink="Events"
          path2={`/event/${eventId}/participant`}
          titleLink2={eventName || 'Event'}
          titleTypo="Logs"
        />
      ) : (
        <Breadcrumb titleTypo="Logs" />
      )}

      <Box sx={{ mb: { xs: 2.5, sm: 3 } }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
            mb: { xs: 0.5, sm: 1 },
          }}>
          Deletion Logs
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
          }}>
          Every activity removed by an admin, with a full snapshot of the record as it was before
          the delete.
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          p: { xs: 1.5, sm: 2 },
          mb: { xs: 2, sm: 2.5 },
        }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 1.5, md: 2 }}
          alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField
            size="small"
            label="Search"
            placeholder="Bib, actor, activity id…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: { md: 240 } }}
          />
          <FormControl size="small" sx={{ minWidth: { md: 200 } }}>
            <InputLabel id="log-actor-filter-label">Actor</InputLabel>
            <Select
              labelId="log-actor-filter-label"
              label="Actor"
              value={actorId}
              onChange={(e) => setActorId(e.target.value)}>
              <MenuItem value="">All actors</MenuItem>
              {userOptions.map((u) => (
                <MenuItem key={u._id} value={u._id}>
                  {u.name || u.username}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            type="date"
            label="From"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            onClick={() => {
              setSearchInput('')
              setActorId('')
              setDateFrom('')
              setDateTo('')
            }}
            sx={{ textTransform: 'none', color: 'text.secondary' }}>
            Reset
          </Button>
        </Stack>
      </Paper>

      {logs.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {logs.error.message}
        </Alert>
      )}

      <LogTableSection
        logs={items}
        totalCount={totalCount}
        loading={logs.loading}
        showEventColumn={!eventId}
        onViewSnapshotClick={setSnapshotRow}
        page={page}
        perPage={perPage}
        pageCount={pageCount}
        onPageChange={setPage}
        onPerPageChange={setPerPage}
      />

      <ActivitySnapshotDialog
        open={Boolean(snapshotRow)}
        onClose={() => setSnapshotRow(null)}
        title={snapshotRow ? `Snapshot — activity ${snapshotRow.activityId}` : 'Snapshot'}
        data={_get(snapshotRow, 'snapshot', null)}
        truncated={Boolean(_get(snapshotRow, 'snapshotTruncated', false))}
      />
    </Box>
  )
}

export default LogsPage
